import { createApi } from "@reduxjs/toolkit/query/react";
import createBaseQuery from "./createBaseQuery";
export function createAuthApi(baseUrl) {
    const authApi = createApi({
        reducerPath: `authApi_${baseUrl}`,
        baseQuery: createBaseQuery(baseUrl),
        tagTypes: [
            "Users",
            "Roles",
            "Permissions",
            "ExternalScopes",
            "LDAPConfig",
            "OIDCConfig"
        ],
        endpoints: (build)=>({
                // User endpoints
                getUsers: build.query({
                    query: ()=>({
                            url: "api/admin/users",
                            method: "GET"
                        }),
                    providesTags: [
                        "Users"
                    ],
                    transformResponse: (response)=>response.users
                }),
                createUser: build.mutation({
                    query: (body)=>({
                            url: "api/admin/users",
                            method: "POST",
                            body
                        }),
                    invalidatesTags: [
                        "Users"
                    ]
                }),
                updateUser: build.mutation({
                    query: ({ user_id, new_username })=>({
                            url: `api/admin/users/${user_id}`,
                            method: "PUT",
                            body: {
                                new_username
                            }
                        }),
                    invalidatesTags: [
                        "Users"
                    ]
                }),
                updateUserPassword: build.mutation({
                    query: ({ user_id, current_password, new_password, new_password2 })=>({
                            url: `api/admin/users/${user_id}/password`,
                            method: "PUT",
                            body: {
                                current_password,
                                new_password,
                                new_password2
                            }
                        }),
                    invalidatesTags: [
                        "Users"
                    ]
                }),
                forcedPasswordChange: build.mutation({
                    query: ({ user_id, new_password, new_password2 })=>({
                            url: `api/admin/users/${user_id}/forced-password-change`,
                            method: "PUT",
                            body: {
                                new_password,
                                new_password2
                            }
                        })
                }),
                resetUserPassword: build.mutation({
                    query: ({ user_id, new_password, new_password2 })=>({
                            url: `api/admin/users/${user_id}/reset-password`,
                            method: "PUT",
                            body: {
                                new_password,
                                new_password2
                            }
                        }),
                    invalidatesTags: [
                        "Users"
                    ]
                }),
                deleteUser: build.mutation({
                    query: ({ user_id })=>({
                            url: `api/admin/users/${user_id}`,
                            method: "DELETE"
                        }),
                    invalidatesTags: [
                        "Users"
                    ]
                }),
                assignRoleToUser: build.mutation({
                    query: ({ user_id, role_id })=>({
                            url: `api/admin/users/${user_id}/roles`,
                            method: "POST",
                            body: {
                                role_id
                            }
                        }),
                    invalidatesTags: [
                        "Users",
                        "Roles"
                    ]
                }),
                removeRoleFromUser: build.mutation({
                    query: ({ user_id, role_id })=>({
                            url: `api/admin/users/${user_id}/roles/${role_id}`,
                            method: "DELETE"
                        }),
                    invalidatesTags: [
                        "Users",
                        "Roles"
                    ]
                }),
                // Role endpoints
                getRoles: build.query({
                    query: ()=>({
                            url: "api/admin/roles",
                            method: "GET"
                        }),
                    providesTags: [
                        "Roles"
                    ],
                    transformResponse: (response)=>response.roles
                }),
                createRole: build.mutation({
                    query: (body)=>({
                            url: "api/admin/roles",
                            method: "POST",
                            body
                        }),
                    invalidatesTags: [
                        "Roles"
                    ]
                }),
                updateRole: build.mutation({
                    query: ({ role_id, new_name, new_description })=>({
                            url: `api/admin/roles/${role_id}`,
                            method: "PUT",
                            body: {
                                new_name,
                                new_description
                            }
                        }),
                    invalidatesTags: [
                        "Roles",
                        "Users"
                    ]
                }),
                deleteRole: build.mutation({
                    query: ({ role_id })=>({
                            url: `api/admin/roles/${role_id}`,
                            method: "DELETE"
                        }),
                    invalidatesTags: [
                        "Roles",
                        "Users"
                    ]
                }),
                assignPermissionToRole: build.mutation({
                    query: ({ role_id, permission_id })=>({
                            url: `api/admin/roles/${role_id}/permissions`,
                            method: "POST",
                            body: {
                                permission_id
                            }
                        }),
                    invalidatesTags: [
                        "Roles",
                        "Users"
                    ]
                }),
                removePermissionFromRole: build.mutation({
                    query: ({ role_id, permission_id })=>({
                            url: `api/admin/roles/${role_id}/permissions/${permission_id}`,
                            method: "DELETE"
                        }),
                    invalidatesTags: [
                        "Roles",
                        "Users"
                    ]
                }),
                // Permission endpoints
                getPermissions: build.query({
                    query: ()=>({
                            url: "api/admin/permissions",
                            method: "GET"
                        }),
                    providesTags: [
                        "Permissions"
                    ],
                    transformResponse: (response)=>response.permissions
                }),
                // External Scope-Role Mapping endpoints
                getExternalScopes: build.query({
                    query: ()=>({
                            url: "api/admin/external-scopes",
                            method: "GET"
                        }),
                    providesTags: [
                        "ExternalScopes"
                    ]
                }),
                getExternalScopeMappings: build.query({
                    query: ()=>({
                            url: "api/admin/external-scopes/mappings",
                            method: "GET"
                        }),
                    providesTags: [
                        "Roles",
                        "ExternalScopes"
                    ],
                    transformResponse: (response)=>response.mappings
                }),
                assignRoleToExternalScope: build.mutation({
                    query: ({ external_scope_id, role_id })=>({
                            url: `api/admin/external-scopes/${encodeURIComponent(external_scope_id)}/roles`,
                            method: "POST",
                            body: {
                                role_id
                            }
                        }),
                    invalidatesTags: [
                        "Roles",
                        "ExternalScopes",
                        "Users"
                    ]
                }),
                removeRoleFromExternalScope: build.mutation({
                    query: ({ external_scope_id, role_id })=>({
                            url: `api/admin/external-scopes/${encodeURIComponent(external_scope_id)}/roles/${encodeURIComponent(role_id)}`,
                            method: "DELETE"
                        }),
                    invalidatesTags: [
                        "Roles",
                        "ExternalScopes",
                        "Users"
                    ]
                }),
                // Auth endpoints
                issueToken: build.mutation({
                    query: (credentials)=>({
                            url: "api/admin/token",
                            method: "POST",
                            body: credentials
                        })
                }),
                // Returns the authenticated user's resolved identity and permissions.
                // Useful after Keycloak login, where the JWT payload structure differs
                // from local tokens and permissions are resolved server-side.
                getMe: build.query({
                    query: ()=>({
                            url: "api/admin/auth/me",
                            method: "GET"
                        })
                }),
                // LDAP config endpoints
                getLdapConfig: build.query({
                    query: ()=>({
                            url: "api/admin/auth/conf/ldap",
                            method: "GET"
                        }),
                    providesTags: [
                        "LDAPConfig"
                    ]
                }),
                configureLdap: build.mutation({
                    query: (body)=>({
                            url: "api/admin/auth/conf/ldap",
                            method: "POST",
                            body
                        }),
                    invalidatesTags: [
                        "Users",
                        "ExternalScopes",
                        "LDAPConfig"
                    ]
                }),
                deleteLdapConfig: build.mutation({
                    query: ()=>({
                            url: "api/admin/auth/conf/ldap",
                            method: "DELETE"
                        }),
                    invalidatesTags: [
                        "Users",
                        "ExternalScopes",
                        "LDAPConfig"
                    ]
                }),
                // OIDC config endpoints
                getOidcConfig: build.query({
                    query: ()=>({
                            url: "api/admin/auth/conf/oidc",
                            method: "GET"
                        }),
                    providesTags: [
                        "OIDCConfig"
                    ]
                }),
                configureOidc: build.mutation({
                    query: (body)=>({
                            url: "api/admin/auth/conf/oidc",
                            method: "POST",
                            body
                        }),
                    invalidatesTags: [
                        "Users",
                        "ExternalScopes",
                        "OIDCConfig"
                    ]
                }),
                deleteOidcConfig: build.mutation({
                    query: ()=>({
                            url: "api/admin/auth/conf/oidc",
                            method: "DELETE"
                        }),
                    // Do NOT invalidate "OIDCConfig" here.  Since getOidcConfig runs
                    // without `skip`, an invalidation would trigger a refetch that hits
                    // the backend (which just deleted the config) and logs a 404 error.
                    // Instead we clear the cached query data directly via onQueryStarted.
                    invalidatesTags: [
                        "Users",
                        "ExternalScopes"
                    ],
                    async onQueryStarted (_, { dispatch, queryFulfilled }) {
                        try {
                            await queryFulfilled;
                            // Overwrite the cached OIDC config with an empty sentinel so the
                            // UI immediately reflects the deletion without a network request.
                            // Immer treats `return undefined` as "no change", so we must
                            // return a concrete (albeit empty) value.  The consumer in
                            // Users.tsx already guards against missing fields, and the next
                            // successful getOidcConfig fetch will replace this sentinel.
                            dispatch(authApi.util.updateQueryData("getOidcConfig", undefined, ()=>({})));
                        } catch  {
                        // Delete failed — leave cache as-is
                        }
                    }
                }),
                // IdP backends healthcheck
                getBackendsHealth: build.query({
                    query: ()=>({
                            url: "api/admin/auth/conf/health",
                            method: "GET"
                        }),
                    providesTags: [
                        "LDAPConfig",
                        "OIDCConfig"
                    ]
                })
            })
    });
    return authApi;
}
